var _excluded = ["selectValue", "placeholder", "viewModePlaceholder", "size", "isDisabled", "isReadOnly", "isViewMode", "isInvalid", "isLoading", "aria-expanded", "id", "onClick", "onFocus", "onBlur"];

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) { symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); } keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys(Object(source), true).forEach(function (key) { _defineProperty(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }

function _defineProperty(obj, key, value) { if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }

function _objectWithoutProperties(source, excluded) { if (source == null) return {}; var target = _objectWithoutPropertiesLoose(source, excluded); var key, i; if (Object.getOwnPropertySymbols) { var sourceSymbolKeys = Object.getOwnPropertySymbols(source); for (i = 0; i < sourceSymbolKeys.length; i++) { key = sourceSymbolKeys[i]; if (excluded.indexOf(key) >= 0) continue; if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue; target[key] = source[key]; } } return target; }

function _objectWithoutPropertiesLoose(source, excluded) { if (source == null) return {}; var target = {}; var sourceKeys = Object.keys(source); var key, i; for (i = 0; i < sourceKeys.length; i++) { key = sourceKeys[i]; if (excluded.indexOf(key) >= 0) continue; target[key] = source[key]; } return target; }

import { Input } from '@chakra-ui/react';
import { isArray, isEmpty } from 'lodash';
import React, { useCallback, useState } from 'react';
import { Loader } from '../../../../foundations/Loader';
import { themeSpaces } from '../../../../theme/foundations';
import { useBoolean, useTestId } from '../../../../utils';
import { useMultiStyleConfig } from '../../../../utils/use-style-config';
import { Text } from '../../../dataDisplay';
import { Box } from '../../Box';
import { useFormControlContext } from '../../FormControl';
import { InputGroup } from '../../InputGroup';
import { InputRightElement } from '../../InputRightElement';
import { TriggerRightIcon } from './TriggerRightIcon';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";

var ToggleButtonComponent = (_ref, ref) => {
  var {
    selectValue,
    placeholder = 'Select',
    viewModePlaceholder,
    size,
    isDisabled,
    isReadOnly,
    isViewMode,
    isInvalid,
    isLoading,
    'aria-expanded': ariaExpanded,
    id,
    onClick,
    onFocus: _onFocus,
    onBlur: _onBlur
  } = _ref,
      props = _objectWithoutProperties(_ref, _excluded);

  var getTestId = useTestId('base-select');
  var [rightElementWidth, setRightElementWidth] = useState(null);
  var rightElementRef = useCallback(ref => {
    if (!ref) return;
    setRightElementWidth(ref.clientWidth);
  }, []);
  var value = isArray(selectValue) ? selectValue.map(_ref2 => {
    var {
      label
    } = _ref2;
    return label;
  }).join(', ') : selectValue === null || selectValue === void 0 ? void 0 : selectValue.label;
  var isFormContext = !isEmpty(useFormControlContext());
  var formFieldPlaceholder = viewModePlaceholder !== null && viewModePlaceholder !== void 0 ? viewModePlaceholder : placeholder;
  var standalonePlaceholder = isViewMode && viewModePlaceholder ? viewModePlaceholder : placeholder;
  var placeholderValue = isFormContext ? formFieldPlaceholder : standalonePlaceholder;
  var disableClick = isDisabled || isLoading || isReadOnly || isViewMode;
  var styles = useMultiStyleConfig('BaseSelect', {
    size,
    isViewMode,
    placeholderValue
  });
  var sharedProps = {
    'aria-disabled': isDisabled || undefined,
    'data-readonly': isReadOnly || isLoading || isViewMode || undefined,
    'data-loading': isLoading || undefined,
    'data-view-mode': isViewMode || undefined,
    'data-invalid': isInvalid || undefined
  };
  var toggleTrigger = useCallback(event => {
    if (disableClick) return;
    event.stopPropagation();
    onClick === null || onClick === void 0 ? void 0 : onClick(event);
  }, [onClick, disableClick]);
  var [triggerIsFocused, setTriggerIsFocused] = useBoolean(false);
  return (
    /*#__PURE__*/
    // Using chakra's `Input` so we can inherit the styles for the button from `form.utils.ts`.
    _jsxs(InputGroup, {
      children: [/*#__PURE__*/_jsx(Input, _objectSpread(_objectSpread(_objectSpread(_objectSpread({
        "data-component": "BaseSelect.ToggleButton",
        ref: ref,
        sx: styles['triggerInput'],
        className: size
      }, getTestId('trigger-input')), props), {}, {
        id: id // using `onMouseDown` to prevent the menu from opening when the user clicking label in form context.
        ,
        onMouseDown: toggleTrigger,
        "aria-expanded": ariaExpanded,
        placeholder: placeholderValue,
        tabIndex: disableClick ? -1 : 0
      }, sharedProps), {}, {
        onFocus: e => {
          if (disableClick) return;
          setTriggerIsFocused.on();
          _onFocus === null || _onFocus === void 0 ? void 0 : _onFocus(e);
        },
        onBlur: e => {
          if (disableClick) return;
          setTriggerIsFocused.off();
          _onBlur === null || _onBlur === void 0 ? void 0 : _onBlur(e);
        }
      })), /*#__PURE__*/_jsx(Input, _objectSpread(_objectSpread(_objectSpread({
        as: "div",
        sx: styles['trigger'],
        className: size
      }, getTestId('trigger-value')), {}, {
        id: "select-trigger-ui".concat(id ? "-".concat(id) : '')
      }, sharedProps), {}, {
        paddingRight: rightElementWidth ? "calc(".concat(rightElementWidth, "px + ").concat(themeSpaces.xs, ")") : 'none',
        "data-focus": triggerIsFocused || undefined // tabIndex is set to -1 to prevent the trigger from being focused when the user presses the tab key
        ,
        tabIndex: -1,
        "aria-hidden": true,
        "data-ui-only": true,
        children: value ? /*#__PURE__*/_jsx(Text, {
          textStyle: "inline",
          color: "inherit",
          shouldSupportEllipsis: true,
          "aria-hidden": true,
          children: value
        }) : /*#__PURE__*/_jsx(Box, {
          __css: styles['placeholder'],
          "data-disabled": isDisabled || undefined,
          "aria-hidden": true
        })
      })), /*#__PURE__*/_jsx(InputRightElement, {
        ref: rightElementRef,
        sx: styles['toggleButtonRightElement'],
        children: isLoading ? /*#__PURE__*/_jsx(Loader, {}) : /*#__PURE__*/_jsx(TriggerRightIcon, {
          size: "small",
          icon: ariaExpanded ? 'caret-up' : 'caret-down',
          isDisabled: isDisabled,
          isReadOnly: isReadOnly,
          isViewMode: isViewMode,
          "aria-hidden": true
        })
      })]
    })
  );
};

export var ToggleButton = /*#__PURE__*/React.forwardRef(ToggleButtonComponent);
ToggleButton.displayName = 'BaseSelect.ToggleButton';
//# sourceMappingURL=ToggleButton.js.map